import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BUFFER_PERIOD, DAYJS_DAYS_OF_WEEK } from "../../../constants";
import {
    updateEmployeeSchedulesForDates,
    employeeSignOut,
    getImageFromStorage,
    getCurrentBusinessConfig,
    markAppointmentComplete,
    generateRecurringBreakBlockersForEmployee,
    fetchEmployeeSchedule,
    getEmployeeWorkHours,
    patchPreferencesConfig
} from "../../../firebase/firebase-delegate";
import { Appointment, AppointmentStatus, BusinessConfig, isUpcomingStatus } from "../../../types";
import { auth } from "../../../firebase/firebase";
import { isAppointmentEndTimePast } from "../../../time/timeUtils";
import { useAdminData } from "../../../contexts/AdminDataContext";
import {
    applyAppointmentChange,
    appointmentsByEmployeeKey,
    employeeScheduleKey,
    ChangeMode,
    type AppointmentsByEmployeeQueryKey,
    type EmployeeScheduleQueryKey,
} from "../../../utils/cache-delegate";
import { useTenant } from "../../../tenancy/TenantContext";
import { getDaysUntilSubscriptionLock, isSubscriptionAccessible } from "../../../utils/subscriptionAccess";
import { confirmUnsavedNavigation } from "../../../utils/unsavedChangesGuard";
import { useTenantOnboardingStatus } from "../../../sitePublishing/hooks";

export const AdminDashboardViewModel = () => {
    const { tenantId } = useTenant();
    const SITE_MAINTENANCE_INTERVAL_MS = BUFFER_PERIOD * 24 * 60 * 60 * 1000;
    const MAINTENANCE_RETRY_INTERVAL_MS = 60 * 60 * 1000;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [lockDrawer, setLockDrawer] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [showAlertBanner, setShowAlertBanner] = useState<boolean>(false);
    const pendingAutoFillKeyRef = useRef<string | null>(null);
    const lastProcessedAutoFillKeyRef = useRef<string | null>(null);

    const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', tenantId],
        queryFn: () => getCurrentBusinessConfig(tenantId),
    });

    const paymentInfo = businessConfigQuery.data?.paymentInfo;
    const isSubscriptionValid = isSubscriptionAccessible(paymentInfo);
    const skipSubscriptionCheck = businessConfigQuery.data?.paymentInfo?.skipSubscriptionCheck === true;
    const currentUserId = auth.currentUser?.uid;
    const isSubscriptionOwner = paymentInfo?.owner === currentUserId;
    const onboardingStatusQuery = useTenantOnboardingStatus(isSubscriptionOwner);
    const isSelfServiceOnboarding =
        onboardingStatusQuery.data?.lifecycleState === 'onboarding';
    // The server's billing verdict also covers a skipped check or an internal
    // override, even when the business settings here were loaded earlier.
    const serverAllowsAccess = onboardingStatusQuery.data?.gates.payment.complete === true;
    const paymentStatus = paymentInfo?.subscriptionStatus ?? '';
    const paymentNeedsAttention = !['active', 'trialing', 'trial'].includes(paymentStatus);
    const paymentLockDaysRemaining = getDaysUntilSubscriptionLock(paymentInfo);
    const showPaymentWarningBanner =
        !skipSubscriptionCheck &&
        paymentNeedsAttention &&
        typeof paymentLockDaysRemaining === 'number' &&
        paymentLockDaysRemaining > 0;

    const superAdminQuery = useQuery({
        queryKey: ['superAdminStatus', auth.currentUser?.uid],
        queryFn: async () => {
            const user = auth.currentUser;

            const token = await user?.getIdTokenResult(true);
            return token?.claims?.superAdmin === true;
        },
        enabled: !!auth.currentUser,
    });

    useEffect(() => {
        if (businessConfigQuery.isLoading || businessConfigQuery.isFetching) return;
        if (superAdminQuery.isLoading) return;
        // Super admins operate every tenant; a tenant's billing state never locks them out.
        if (superAdminQuery.data === true) {
            setLockDrawer(false);
            if (location.pathname === '/admin/missing-subscription') navigate('/admin', { replace: true });
            return;
        }

        const config = businessConfigQuery.data;
        const currentUserId = auth.currentUser?.uid;

        if (!config || !currentUserId) return;

        const ownerId = config.paymentInfo?.owner;
        const isOwner = ownerId === currentUserId;
        const isOnMissingSubscriptionPage = location.pathname === '/admin/missing-subscription';

        if (isOwner && (onboardingStatusQuery.isLoading || onboardingStatusQuery.isFetching)) return;

        if (isSelfServiceOnboarding) {
            setLockDrawer(false);
            if (location.pathname !== '/admin/onboarding') {
                navigate('/admin/onboarding', { replace: true });
            }
            return;
        }

        const shouldLock = !isSubscriptionValid && !serverAllowsAccess &&
            config.paymentInfo?.skipSubscriptionCheck !== true;
        setLockDrawer(shouldLock);

        if (shouldLock) {
            if (!isOwner && location.pathname !== '/admin/missing-subscription') {
                navigate('/admin/missing-subscription');
            } else if (isOwner && location.pathname !== '/admin/manage-subscription') {
                navigate('/admin/manage-subscription');
            }
            return;
        }

        // If subscription becomes valid again, do not keep non-owners stuck on the lock screen.
        if (isOnMissingSubscriptionPage) {
            navigate('/admin', { replace: true });
        }
    }, [
        businessConfigQuery.isLoading,
        businessConfigQuery.isFetching,
        businessConfigQuery.data,
        superAdminQuery.isLoading,
        superAdminQuery.data,
        isSubscriptionValid,
        serverAllowsAccess,
        isSelfServiceOnboarding,
        onboardingStatusQuery.isLoading,
        onboardingStatusQuery.isFetching,
        navigate,
        location.pathname
    ]);



    const {
        currentEmployeeQuery: employeeQuery,
        employeesQuery,
        hydrateAppointmentsQuery,
        myAppointments,
        scheduleQuery,
        workHoursQuery,
        appointmentIds,
    } = useAdminData();

    const employeeId = employeeQuery.data?.id;
    // Match the AdminDataProvider query key so our local cache updates never drift
    const appointmentsQueryKey = useMemo<AppointmentsByEmployeeQueryKey | null>(
        () => (employeeId ? appointmentsByEmployeeKey(tenantId, employeeId, appointmentIds) : null),
        [tenantId, employeeId, appointmentIds]
    );
    const scheduleQueryKey = useMemo<EmployeeScheduleQueryKey | null>(
        () => (employeeId ? employeeScheduleKey(tenantId, employeeId) : null),
        [tenantId, employeeId]
    );

    const finalAlertMessage = "Your default saved hours was used to update availability.";

    const dismissAlert = () => setShowAlertBanner(false);

    const updatePassedAppointmentsAsComplete = useMutation({
        mutationFn: async (): Promise<Appointment[]> => {
            if (!employeeQuery.data?.id || !hydrateAppointmentsQuery.data) {
                throw new Error('Missing data needed to save.');
            }

            // Fetch active appointments
            const activeAppointments = myAppointments.filter((app) => {
                return isUpcomingStatus(app.status) && app.employeeID === employeeQuery.data?.id;
            });

            // This filters to appointments that have passed their end time
            const appointmentsToMarkComplete = activeAppointments.filter((app) => isAppointmentEndTimePast(app));
            const completed: Appointment[] = [];

            for (const app of appointmentsToMarkComplete) {
                try {
                    await markAppointmentComplete(app, employeeQuery.data.id, scheduleQuery.data ?? undefined, tenantId);
                    completed.push(app);
                } catch (err) {
                    console.error(`Failed to complete appointment ${app.id}:`, err);
                }
            }
            return completed;
        },
        onSuccess: (completedAppointments) => {
            if (!completedAppointments?.length || !appointmentsQueryKey) {
                return;
            }
            completedAppointments.forEach((appointment) => {
                applyAppointmentChange(
                    queryClient,
                    ChangeMode.REPLACE,
                    /*appointment=*/{ ...appointment, status: AppointmentStatus.COMPLETE },
                    appointment,
                    /*options=*/ {
                        siteId: tenantId,
                        appointmentsQueryKey: appointmentsQueryKey ?? undefined,
                        scheduleKey: scheduleQueryKey ?? undefined,
                        serverAlreadyUpdatedScheduleBitstrings: true,
                    }
                );
            });
        },
        onError: (error) => {
            console.error('Error updating schedule:', error);
        },
    });

    // Tracks the date/bitstring pair for each auto-filled schedule so we can update caches deterministically.
    type AutoFilledScheduleUpdate = {
        dateKey: string;
        bitString: string;
    };

    // Mutation for updating the schedule and canceling appointments
    const updateEachMissingDateWithCurrentWorkHours = useMutation({
        mutationFn: async (): Promise<AutoFilledScheduleUpdate[]> => {
            if (!employeeQuery.data?.id || !workHoursQuery.data || !scheduleQuery.data) {
                throw new Error('Missing data needed to save. Please Refresh the page and try again.');
            }
            const workHoursTemplate = workHoursQuery.data;
            // Include an extended buffer so we quietly fix gaps slightly further out.
            const missingDatesInExtendedBuffer = CheckForMissingWorkHoursInBuffer(scheduleQuery.data, BUFFER_PERIOD * 2);

            if (missingDatesInExtendedBuffer.length === 0) {
                return [];
            }

            // Convert MMDDYYYY strings into Dayjs instances for the batch delegate helper.
            const missingDateInstances = missingDatesInExtendedBuffer.map((missingDateKey) => dayjs(missingDateKey, 'MMDDYYYY'));

            const scheduleUpdates = await updateEmployeeSchedulesForDates(employeeQuery.data.id, workHoursTemplate, missingDateInstances, tenantId);

            const windowStart = dayjs(missingDatesInExtendedBuffer[0], 'MMDDYYYY');
            const windowEnd = dayjs(missingDatesInExtendedBuffer[missingDatesInExtendedBuffer.length - 1], 'MMDDYYYY');

            // Ensure recurring break blockers exist for the auto-filled dates so availability stays accurate.
            await generateRecurringBreakBlockersForEmployee({
                employeeId: employeeQuery.data.id,
                windowStart,
                windowEnd,
                siteId: tenantId,
            });

            return missingDatesInExtendedBuffer.map((dateKey) => {
                const parsedDate = dayjs(dateKey, 'MMDDYYYY');
                const weekdayKey = DAYJS_DAYS_OF_WEEK[parsedDate.day()];
                const bitString = scheduleUpdates[dateKey] ?? workHoursTemplate[weekdayKey];
                return { dateKey, bitString };
            });
        },
        onSuccess: async (updates) => {
            const processedKey = pendingAutoFillKeyRef.current;
            if (processedKey) {
                lastProcessedAutoFillKeyRef.current = processedKey;
                pendingAutoFillKeyRef.current = null;
            }
            setShowAlertBanner(true);
            setAlertMessage(finalAlertMessage);
            // Force refetches so any consumers relying on cached schedule or appointment maps pick up generated blockers.
            // This is better than just reloading the page because it's less disruptive to the user.
            const refreshPromises: Promise<unknown>[] = [];
            if (typeof scheduleQuery.refetch === 'function') {
                refreshPromises.push(scheduleQuery.refetch());
            }
            if (typeof employeeQuery.refetch === 'function') {
                refreshPromises.push(employeeQuery.refetch());
            }
            if (refreshPromises.length > 0) {
                console.log('Refreshing schedule and employee data after auto-fill.');
                await Promise.allSettled(refreshPromises);
            }
        },
        onError: (error) => {
            console.error('Error updating schedule:', error);
            setAlertMessage("We tried to apply your default schedule but ran into an issue. Please review your work hours manually.");
            setShowAlertBanner(true);
            pendingAutoFillKeyRef.current = null;
        },
    });

    const checkForMissingWorkHoursMutation = useMutation({
        mutationFn: async () => {
            if (!scheduleQuery.data) return [];
            return CheckForMissingWorkHoursInBuffer(scheduleQuery.data, BUFFER_PERIOD);
        },
        onSuccess: (missingDates) => {
            if (missingDates.length === 0) {
                if (!lastProcessedAutoFillKeyRef.current) {
                    setShowAlertBanner(false);
                    setAlertMessage("");
                }
                pendingAutoFillKeyRef.current = null;
                return;
            }

            const missingKey = missingDates.join(',');
            const alreadyProcessed = lastProcessedAutoFillKeyRef.current === missingKey;
            const alreadyPending = pendingAutoFillKeyRef.current === missingKey;

            if (alreadyProcessed) {
                setAlertMessage(finalAlertMessage);
                setShowAlertBanner(true);
                return;
            }

            if (!alreadyPending && !updateEachMissingDateWithCurrentWorkHours.isPending) {
                pendingAutoFillKeyRef.current = missingKey;
                updateEachMissingDateWithCurrentWorkHours.mutate();
                return;
            }

            setShowAlertBanner(false);
            setAlertMessage("");
        },
        onError: (error) => {
            console.error('Error checking for missing work hours:', error);
        },
    });

    type SiteMaintenanceSummary = {
        updatedEmployees: number;
        updatedDates: number;
        failures: number;
    };

    const persistMaintenanceTimestamps = async (lastRunAt: number, nextRunAt: number) => {
        try {
            await patchPreferencesConfig({
                lastEmployeeScheduleMaintenanceAt: lastRunAt,
                nextEmployeeScheduleMaintenanceAt: nextRunAt,
            }, tenantId);
            queryClient.setQueryData<BusinessConfig | null>(['businessConfig', tenantId], (old) => {
                if (!old) return old;
                return {
                    ...old,
                    preferences: {
                        ...(old.preferences || {}),
                        lastEmployeeScheduleMaintenanceAt: lastRunAt,
                        nextEmployeeScheduleMaintenanceAt: nextRunAt,
                    },
                };
            });
        } catch (persistError) {
            console.error('Failed to persist schedule maintenance timestamps:', persistError);
        }
    };

    const maintainAllActiveEmployeeSchedulesMutation = useMutation({
        mutationFn: async (): Promise<SiteMaintenanceSummary> => {
            if (!employeeQuery.data?.admin) {
                return { updatedEmployees: 0, updatedDates: 0, failures: 0 };
            }
            if (!employeesQuery.data?.length) {
                return { updatedEmployees: 0, updatedDates: 0, failures: 0 };
            }

            const employeesToMaintain = employeesQuery.data.filter((employee) => employee.active !== false);
            let updatedEmployees = 0;
            let updatedDates = 0;
            let failures = 0;

            for (const employee of employeesToMaintain) {
                try {
                    const [workHoursTemplate, schedule] = await Promise.all([
                        getEmployeeWorkHours(employee.id, tenantId),
                        fetchEmployeeSchedule(employee.id, tenantId),
                    ]);
                    const missingDatesInExtendedBuffer = CheckForMissingWorkHoursInBuffer(schedule, BUFFER_PERIOD * 2);
                    if (missingDatesInExtendedBuffer.length === 0) {
                        continue;
                    }

                    const missingDateInstances = missingDatesInExtendedBuffer.map((missingDateKey) =>
                        dayjs(missingDateKey, 'MMDDYYYY')
                    );
                    await updateEmployeeSchedulesForDates(employee.id, workHoursTemplate, missingDateInstances, tenantId);

                    const windowStart = dayjs(missingDatesInExtendedBuffer[0], 'MMDDYYYY');
                    const windowEnd = dayjs(missingDatesInExtendedBuffer[missingDatesInExtendedBuffer.length - 1], 'MMDDYYYY');
                    await generateRecurringBreakBlockersForEmployee({
                        employeeId: employee.id,
                        windowStart,
                        windowEnd,
                        siteId: tenantId,
                    });

                    updatedEmployees += 1;
                    updatedDates += missingDatesInExtendedBuffer.length;
                } catch (error) {
                    failures += 1;
                    console.error(`Failed site-wide schedule maintenance for employee ${employee.id}`, error);
                }
            }

            return { updatedEmployees, updatedDates, failures };
        },
        onSuccess: async (summary) => {
            const now = Date.now();
            const nextRunAt = now + (summary.failures > 0
                ? MAINTENANCE_RETRY_INTERVAL_MS
                : SITE_MAINTENANCE_INTERVAL_MS);
            await persistMaintenanceTimestamps(now, nextRunAt);

            if (summary.updatedEmployees > 0) {
                setAlertMessage(
                    `Schedule maintenance updated ${summary.updatedEmployees} employee(s) across ${summary.updatedDates} day(s).`
                );
                setShowAlertBanner(true);
            }
            if (summary.failures > 0) {
                setAlertMessage(
                    `Schedule maintenance completed with ${summary.failures} employee error(s). Review console logs for details.`
                );
                setShowAlertBanner(true);
            }
            if (summary.updatedEmployees > 0 && typeof scheduleQuery.refetch === 'function') {
                await scheduleQuery.refetch();
            }
        },
        onError: async (error) => {
            console.error('Site-wide schedule maintenance failed:', error);
            const now = Date.now();
            const nextRunAt = now + MAINTENANCE_RETRY_INTERVAL_MS;
            await persistMaintenanceTimestamps(now, nextRunAt);
            setAlertMessage("We couldn't run team schedule maintenance. Please try refreshing the dashboard.");
            setShowAlertBanner(true);
        },
    });


    // These should run on page load
    useEffect(() => {
        // Makes the link and tab name "Admin Dashboard"
        if (businessConfigQuery.data?.businessInfo?.name) {
            document.title = businessConfigQuery.data.businessInfo.name + " Admin Dashboard";
        }
    }, [businessConfigQuery.data]);

    useEffect(() => {
        //check for appointments that are past the cutoff time and mark them as complete
        if (
            employeeQuery.data?.id &&
            hydrateAppointmentsQuery.data &&
            myAppointments &&
            !updatePassedAppointmentsAsComplete.isPending &&
            !updatePassedAppointmentsAsComplete.isSuccess &&
            !updatePassedAppointmentsAsComplete.isError
        ) {
            updatePassedAppointmentsAsComplete.mutate();
        }
    }, [employeeQuery.data, myAppointments]);


    const profilePicturePath = employeeQuery.data?.profilePictureImagePath ?? null;

    useEffect(() => {
        if (!profilePicturePath) {
            setProfilePicUrl(null);
            return;
        }

        getImageFromStorage(profilePicturePath)
            .then((url) => setProfilePicUrl(url))
            .catch((error) => {
                console.log("Error fetching profile picture: ", error);
                setProfilePicUrl(null);
            });
    }, [profilePicturePath, employeeQuery.dataUpdatedAt]);

    // Navigate to specific routes within the dashboard
    const navigateTo = (path: string) => {
        if (!confirmUnsavedNavigation()) return;
        const tenantOverride = new URLSearchParams(location.search).get('tenant');
        navigate({
            pathname: `/admin/${path}`,
            search: tenantOverride ? `?tenant=${encodeURIComponent(tenantOverride)}` : '',
        });
        setDrawerOpen(false);
    };

    const sendTo404Page = () => {
        navigate('/error');
    };

    // Toggle Drawer
    const toggleDrawer = (open: boolean) => setDrawerOpen(open);

    // Sign out handler
    const handleSignOut = async () => {
        if (!confirmUnsavedNavigation()) return;
        try {
            await employeeSignOut();
            queryClient.invalidateQueries();
            navigate('/admin');
        } catch {
            sendTo404Page();
        }
    };


    // Loop through the next X days and make sure we notify them if they should be updating schedule
    function CheckForMissingWorkHoursInBuffer(appointments: Map<string, string>, bufferPeriod: number = BUFFER_PERIOD) {
        const today = dayjs.tz();
        let missingDays: Array<string> = [];

        for (let i = 0; i <= bufferPeriod; i++) {
            const dateToCheck = today.add(i, "day").format("MMDDYYYY");
            if (!appointments.has(dateToCheck)) {
                missingDays.push(dateToCheck);
            }
        }

        return missingDays;
    }

    const hasDashboardAccess = isSubscriptionValid || skipSubscriptionCheck || serverAllowsAccess || isSelfServiceOnboarding;
    // Only the data the dashboard needs blocks it. Housekeeping (completing past
    // appointments, filling missing schedule days) runs behind a small banner, so
    // one slow or stalled write never leaves the whole dashboard on a spinner.
    const isLoading =
        businessConfigQuery.isLoading ||
        (isSubscriptionOwner && onboardingStatusQuery.isLoading) ||
        (hasDashboardAccess && (
            employeeQuery.isLoading ||
            scheduleQuery.isLoading ||
            workHoursQuery.isLoading
        ));
    const isUpdatingSchedule =
        updatePassedAppointmentsAsComplete.isPending ||
        updateEachMissingDateWithCurrentWorkHours.isPending;
    const isSetUpError = hasDashboardAccess && (employeeQuery.isError || scheduleQuery.isError);

    return {
        drawerOpen,
        lockDrawer,
        alertMessage,
        showPaymentWarningBanner,
        paymentLockDaysRemaining,
        isSubscriptionOwner,
        location,
        showAlertBanner,
        dismissAlert,
        profilePicUrl,
        employeeQuery,
        employeesQuery,
        superAdminQuery,
        scheduleQuery,
        businessConfigQuery,
        onboardingStatusQuery,
        isSelfServiceOnboarding,
        checkForMissingWorkHoursMutation,
        maintainAllActiveEmployeeSchedulesMutation,
        toggleDrawer,
        navigateTo,
        sendTo404Page,
        handleSignOut,
        isLoading,
        isUpdatingSchedule,
        isSetUpError,
    };
};
